<template>
  <div class="bidlist">
    <div class="d-flex mb-4">
      <DateFilter @change-date="setDateFilter" @change="applyDateFilter" :value="dateFilter" />
      <v-btn class="mr-2 btn-icon" outlined @click="showStatusFilter = !showStatusFilter">
        <v-badge dot overlap color="error" offset-x="8" bordered :value="hasStatusFilter">
          <v-icon class="mr-md-1">mdi-filter-outline</v-icon>
        </v-badge>
        <span class="d-none d-md-inline">Фильтры</span>
      </v-btn>
      <v-btn @click="loadApps" :disabled="!canLoadApps" class="ag-btn mr-2 ml-auto btn-icon" outlined>
        <v-icon class="mr-md-1">mdi-download-outline</v-icon>
        <span class="d-none d-md-inline">Выгрузить заявки</span>
      </v-btn>
      <v-btn :disabled="!canCreateApp" color="primary" @click="showNewBidModal(true)">+ новая заявка</v-btn>
    </div>
    <v-data-table
      :mobile-breakpoint="100"
      class="bidlist-table"
      :headers="headers"
      :items="bids"
      item-key="id"
      :server-items-length="total"
      :options.sync="options"
      :footer-props="footerProps"
      :no-data-text="noDataText"
    >
      <template #top>
        <v-text-field
          dense
          single-line
          hide-details
          outlined
          v-model="search_query"
          label="Введите ИНН, имя клиента или номер заявки"
          class="mb-4"
          clearable
        ></v-text-field>
      </template>

      <template #[`item.dadd`]="{ value }">
        <span>
          <span>{{ new Date(value).toLocaleDateString() }}</span
          ><br />
          <span class="font font--label-light font--color_light">{{ new Date(value).toLocaleTimeString() }}</span>
        </span>
      </template>

      <template #[`item.stg`]="{ value }">
        <v-chip class="ma-1" :color="productTypesList[v].bgColor" :text-color="productTypesList[v].textColor" small
                v-for="v in stgFiltered(value)" :key="v">{{ productTypesList[v].label }}
        </v-chip>
      </template>

      <template #[`item.client_name`]="{ value, item }">
        <span>
          <span>{{ value }}</span
          ><br />
          <span class="font font--label-light font--color_light" v-if="item.inn"> ИНН: {{ item.inn }} </span>
        </span>
      </template>

      <template #[`item.state`]="{ value }">
        <StatusChip :value="value" />
      </template>

      <template #[`item.person_phone`]="{ value }">
        <span style="white-space: nowrap">{{ formatPhone(value) }}</span>
      </template>

      <template #[`item.id`]="{ value, item }">
        <v-btn :data-value="value" text icon @click="openBidDetails(value, item)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <transition name="slide">
      <StatusFilter
        @input="applyStatusFilter"
        :list="statusList"
        filterTitle="Статус заявки"
        v-show="showStatusFilter"
        :value="statusFilter"
        v-click-outside="{
          handler: closeStatusFilter,
          closeConditional: shouldCloseFilter,
        }"
      />
    </transition>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import StatusChip from "@/components/bids/StatusChip";
import DateFilter from "@/components/common/DateFilter";
import StatusFilter from "@/components/common/StatusFilter";

import { getBid } from "@/api/bids";
import { formatPhone } from "@/utils/helpers";

export default {
  props: {
    items: Array,
  },

  components: {
    StatusChip,
    StatusFilter,
    DateFilter,
  },

  data() {
    return {
      showStatusFilter: false,
      footerProps: {
        itemsPerPageText: "Заявок на странице:",
        itemsPerPageAllText: "Все",
        pageText: "{0}-{1} из {2}",
        itemsPerPageOptions: [5, 10, 15],
      },
      noDataText: "Нет заявок",
      productTypesList: {
        ACCOPEN: {
          label: "РКО",
          bgColor: "#D4F296",
          textColor: "#5E8C00",
        },
        ACCOPEN_VOID: {
          label: "ACCOPEN_VOID",
          bgColor: "#D4F296",
          textColor: "#5E8C00",
        },
        ACCOPENMAN: {
          label: "РКО",
          bgColor: "#D4F296",
          textColor: "#5E8C00",
        },
        "MERCHANT.IA": {
          label: "ИЭ",
          bgColor: "#FAE6AA",
          textColor: "#B79310",
        },
        MODULKASSA: {
          label: "Касса",
          bgColor: "#F9C1BD",
          textColor: "#A61D1D",
        },
        "MERCHANT.TA": {
          label: "ТЭ",
          bgColor: "#FAE6AA",
          textColor: "#B79310",
        },
        "FNS.REG": {
          label: "Рег",
          bgColor: "#EEEEEE",
          textColor: "#808080",
        },
        HICE_ACCOPEN: {
          label: "Хайс: рко",
          bgColor: "#D4F296",
          textColor: "#5E8C00",
        },
        MRPL: {
          label: "Маркетплейс",
          bgColor: "#4971d0",
          textColor: "#FFFFFF",
        },
        CLOUDPAY: {
          label: 'Облачные чеки',
          bgColor: "#FE9B62",
          textColor: "#000000",
        },
        LANDING_PAGE: {
          label: 'Реф. клиент',
          bgColor: "#efd949",
          textColor: "#403b17",
        }
      },
    };
  },

  computed: {
    ...mapState("bid", ["total", "bids", "statuses"]),
    ...mapGetters("profile", ["canCreateApp", "canLoadApps", "canSeeProcess"]),
    ...mapGetters("bid", ["hasStatusFilter", "statusFilter", "dateFilter"]),

    statusList() {
      return [
        {
          label: "Все статусы",
          value: "all",
        },
        // мапим статусы из store
        ...Object.entries(this.statuses).map(([value, item]) => ({ value, label: item.label })),
      ];
    },

    search_query: {
      get() {
        return this.$store.state.bid.search_query;
      },
      set(value) {
        this.$store.commit("bid/setSearchQuery", value);
      },
    },

    options: {
      get() {
        return {
          page: this.$store.state.bid.page,
          itemsPerPage: this.$store.state.bid.pageSize,
        };
      },
      set({ page, itemsPerPage }) {
        this.set({ type: "page", value: page });
        this.set({ type: "pageSize", value: itemsPerPage });
      },
    },

    headers() {
      return [
        {
          text: "№\xa0заявки",
          value: "num",
          sortable: false,
        },
        {
          text: "Продукт",
          value: "stg",
          sortable: false,
        },
        {
          text: "Дата",
          value: "dadd",
          sortable: false,
        },
        { text: "Клиент", value: "client_name", sortable: false },
        { text: "Статус", value: "state", sortable: false },
        { text: "Телефон клиента", value: "person_phone", sortable: false },
        { value: "id", sortable: false },
      ];
    },
  },

  watch: {
    search_query() {
      this.debouncedSearch();
    },

    options: {
      handler() {
        this.getBids();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("meeting", ["getMeetingById"]),
    stgFiltered(stgValue) {
      return stgValue.filter(x => this.productTypesList[x]);
    },
    closeStatusFilter() {
      this.showStatusFilter = false;
    },
    shouldCloseFilter() {
      return this.showStatusFilter;
    },
    ...mapMutations("bid", ["setDateFilter", "setUserInputFilter", "set", "setStatusFilter", "setReserveMeeting"]),
    ...mapMutations("common", ["showNewBidModal", "showStatusFilterModal"]),
    ...mapActions("bid", ["getBids"]),

    formatPhone(value) {
      return formatPhone(value);
    },

    applyDateFilter({ start, end }) {
      this.setDateFilter({ start, end });
      this.options = { page: 1, itemsPerPage: this.options.itemsPerPage };
      this.getBids();
    },

    applyStatusFilter(status) {
      this.setStatusFilter(status);
      this.options = { page: 1, itemsPerPage: this.options.itemsPerPage };
      this.getBids();
      this.closeStatusFilter();
    },

    applyUserInputFilter() {
      this.options = { page: 1, itemsPerPage: this.options.itemsPerPage };
      this.getBids();
    },

    async openBidDetails(id, item) {
      if (item.state === "meeting.process" && this.canSeeProcess) {
        this.getMeetingById(id);
      } else {
        this.$store.commit("bid/setBidDetails", null);
        const response = await getBid(id);
        if (response) {
          this.setReserveMeeting(response.templatedata?.reserve_meeting);
          this.$store.commit("bid/setBidDetails", response);
          this.set({ type: "showBidDetails", value: true });
          this.set({ type: "bidId", value: id });
          this.$store.commit("bid/setFl", response.templatedata?.fl);
        }
      }
    },

    loadApps() {
      console.error("Метод выгрузки заявок не реализован");
    },
  },

  created() {
    this.debouncedSearch = debounce(this.applyUserInputFilter, 500);
  },
};
</script>

<style lang="scss">
.theme--light.v-data-table.bidlist-table > .v-data-table__wrapper > table > thead > tr > th {
  color: #777c87;
}

.v-data-table.bidlist-table > .v-data-table__wrapper > table > thead > tr > th {
  text-transform: uppercase;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0.1em;
  font-weight: 400;
  height: auto;
  padding: 12px 16px;
}

.v-data-table.bidlist-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 16px;
}

.v-data-table-header {
  background-color: #f5f5f6;
}

.slide-leave-active,
.slide-enter-active {
  transition: 0.5s;
}

.slide-enter {
  transform: translate(100%, 0);
}

.slide-leave-to {
  transform: translate(100%, 0);
}
</style>
